import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import AdminNavbar from "../components/AdminNavbar";
import "./AdminProducts.css";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Admin Products | Balitah";

    let descriptionTag = document.querySelector('meta[name="description"]');
    if (!descriptionTag) {
      descriptionTag = document.createElement("meta");
      descriptionTag.setAttribute("name", "description");
      document.head.appendChild(descriptionTag);
    }

    descriptionTag.setAttribute(
      "content",
      "Review and manage listings on Balitah, a local Bruneian e-commerce platform.",
    );
  }, []);

  const toDisplayPercent = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "0%";
    return Number.isInteger(num) ? `${num}%` : `${num.toFixed(2)}%`;
  };

  const getDiscountedPrice = (price, discountRate) => {
    const basePrice = Number(price);
    const rate = Number(discountRate || 0);
    if (!Number.isFinite(basePrice) || basePrice < 0) return 0;
    if (!Number.isFinite(rate) || rate <= 0) return basePrice;
    return Math.round(basePrice * (1 - Math.min(100, rate) / 100) * 100) / 100;
  };

  const fetchProducts = async () => {
    try {
      const data = await api.getAdminProducts();
      setProducts(data.data || []);
    } catch (error) {
      console.error("Failed to load products:", error);
      alert("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const deleteProduct = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this product?",
    );
    if (!confirmed) return;

    setDeletingId(id);
    try {
      await api.deleteAdminProduct(id);
      await fetchProducts();
    } catch (error) {
      console.error("Failed to delete product:", error);
      alert("Failed to delete product");
    } finally {
      setDeletingId(null);
    }
  };

  const normalizedSearch = searchText.trim().toLowerCase();
  const filteredProducts = products.filter((p) => {
    if (!normalizedSearch) return true;
    const name = (p.name || "").toLowerCase();
    const seller = (p.seller?.username || "").toLowerCase();
    return name.includes(normalizedSearch) || seller.includes(normalizedSearch);
  });

  return (
    <div className="ap-page">
      <AdminNavbar />

      <div className="ap-container">
        {/* Header */}
        <div className="ap-header">
          <div className="ap-header-content">
            <h1>Product Management</h1>
            <p>View, manage, and remove products from the marketplace</p>
          </div>
          <div className="ap-header-stats">
            <div className="ap-stat">
              <span className="ap-stat-value">{products.length}</span>
              <span className="ap-stat-label">Total Products</span>
            </div>
          </div>
        </div>

        {/* Search & Info */}
        <div className="ap-toolbar">
          <div className="ap-search-wrap">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="ap-search"
              placeholder="Search products or sellers..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            {searchText && (
              <button
                className="ap-clear-btn"
                onClick={() => setSearchText("")}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
          <div className="ap-hint">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            Click any row to view full details
          </div>
        </div>

        {/* Results Info */}
        {!loading && searchText && (
          <div className="ap-results-info">
            Found <strong>{filteredProducts.length}</strong> products
          </div>
        )}

        {/* Table */}
        <div className="ap-table-card">
          {loading ? (
            <div className="ap-loading">
              <div className="ap-spinner"></div>
              <p>Loading products...</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="ap-empty">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                <line x1="12" y1="22.08" x2="12" y2="12" />
              </svg>
              <h3>No products found</h3>
              <p>
                {searchText
                  ? "Try a different search term"
                  : "No products in the system yet"}
              </p>
            </div>
          ) : (
            <div className="ap-table-wrap">
              <table className="ap-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Pricing</th>
                    <th>Details</th>
                    <th>Seller</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((p) => {
                    const hasDiscount = Number(p.discountRate || 0) > 0;
                    const imageUrl =
                      Array.isArray(p.images) && p.images[0]
                        ? typeof p.images[0] === "string"
                          ? p.images[0]
                          : p.images[0]?.url
                        : p.image;
                    const imageCount = Array.isArray(p.images)
                      ? p.images.length
                      : p.image
                        ? 1
                        : 0;
                    const variantCount = Array.isArray(p.variants)
                      ? p.variants.length
                      : 0;

                    return (
                      <tr
                        key={p._id}
                        onClick={() => navigate(`/admin/products/${p._id}`)}
                        className="ap-row-clickable"
                      >
                        <td>
                          <div className="ap-product-cell">
                            <div className="ap-product-image">
                              {imageUrl ? (
                                <img src={imageUrl} alt={p.name} />
                              ) : (
                                <div className="ap-no-image">
                                  <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                  >
                                    <rect
                                      x="3"
                                      y="3"
                                      width="18"
                                      height="18"
                                      rx="2"
                                      ry="2"
                                    />
                                    <circle cx="8.5" cy="8.5" r="1.5" />
                                    <polyline points="21 15 16 10 5 21" />
                                  </svg>
                                </div>
                              )}
                              {hasDiscount && (
                                <span className="ap-discount-tag">
                                  -{toDisplayPercent(p.discountRate)}
                                </span>
                              )}
                            </div>
                            <div className="ap-product-info">
                              <h4>{p.name}</h4>
                              <p>
                                {p.description
                                  ? p.description.length > 60
                                    ? `${p.description.slice(0, 60)}...`
                                    : p.description
                                  : "No description"}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="ap-pricing">
                            {hasDiscount ? (
                              <>
                                <span className="ap-price-current">
                                  BND{" "}
                                  {getDiscountedPrice(
                                    p.price,
                                    p.discountRate,
                                  ).toFixed(2)}
                                </span>
                                <span className="ap-price-original">
                                  BND {Number(p.price).toFixed(2)}
                                </span>
                              </>
                            ) : (
                              <span className="ap-price-current">
                                BND {Number(p.price).toFixed(2)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="ap-details">
                            <div className="ap-detail-row">
                              <span className="ap-detail-label">
                                Categories
                              </span>
                              <span className="ap-detail-value">
                                {Array.isArray(p.categories) &&
                                p.categories.length > 0
                                  ? p.categories.slice(0, 2).join(", ") +
                                    (p.categories.length > 2 ? "..." : "")
                                  : "-"}
                              </span>
                            </div>
                            <div className="ap-detail-row">
                              <span className="ap-detail-label">Variants</span>
                              <span className="ap-detail-value">
                                {variantCount}
                              </span>
                            </div>
                            <div className="ap-detail-row">
                              <span className="ap-detail-label">Images</span>
                              <span className="ap-detail-value">
                                {imageCount}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="ap-seller">
                            <div className="ap-seller-avatar">
                              {(p.seller?.username || "S")
                                .charAt(0)
                                .toUpperCase()}
                            </div>
                            <span>{p.seller?.username || "Unknown"}</span>
                          </div>
                        </td>
                        <td>
                          <button
                            className="ap-delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteProduct(p._id);
                            }}
                            disabled={deletingId === p._id}
                          >
                            {deletingId === p._id ? (
                              <div className="ap-btn-spinner"></div>
                            ) : (
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                            )}
                            <span>Delete</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
